import styled, { css } from 'styled-components';
import { applyStyleModifiers, type ModifiersConfig, type ModifierKeys } from 'styled-components-modifiers';
import { rgba } from 'polished';

// Import styles
import { flex, customMinResponsive, size } from 'components/styles';

// Import types
import { Modifiers } from 'types/views/styles';

type ContainerProps = {
	width: number;
	modifiers: ModifierKeys; 
	isPromotion: boolean;
};

const PRICE_INFO_MODIFIERS: Modifiers<ContainerProps> = {
	main: () => css`
		${CurrentPrice} {
			font-size: 0.95em;
		}
		${Price} {
			font-size: 2em;
		}
		${PricePeriod} {
			font-size: 1.6rem;

			${customMinResponsive(
				450,
				css`
					font-size: 1.7rem;
				`
			)}

			${customMinResponsive(
				1650,
				css`
					font-size: 2rem;
				`
			)}
		}
		${PriceDisclaimer} {
			font-size: 0.9em;
		}
	`,
	premium: ({ width, theme }) => css`
		width: 100%;

		${customMinResponsive(
			size.semi,
			css`
				width: ${width}%;
			`
		)}

		${CurrentPrice} {
			font-size: 0.85em;
			font-weight: ${theme.font.distinct};
			${customMinResponsive(
				size.semi,
				css`
					font-size: 0.8em;
					font-weight: ${theme.font.normal};
				`
			)};
		}
		${Price} {
			font-size: 2em;
			font-weight: ${theme.font.normal};
			${customMinResponsive(
				size.semi,
				css`
					font-weight: ${theme.font.distinct};
					font-size: 1.6em;
				`
			)}
		}
		${PricePeriod} {
			font-size: 0.625em;
			font-weight: ${theme.font.distinct};
		}
		${PriceDisclaimer} {
			font-size: 0.85em;
			font-weight: ${theme.font.distinct};
			${customMinResponsive(
				size.semi,
				css`
					font-size: 0.6em;
					font-weight: ${theme.font.normal};
				`
			)}
		}
	`,
	summary: ({ theme, isPromotion }) => css`
		width: auto;

		${CurrentPrice} {
			font-size: 0.7em;
		}
		${Price} {
			font-size: 0.9em;
		}
		${PricePeriod} {
			font-size: 1em;
			margin-bottom: 0;
			color: ${isPromotion ? theme.configurator.color.contrast : rgba(theme.configurator.color.primary, 0.9)};
		}
		${PriceDisclaimer} {
			font-size: 0.7em;
		}
	`,
	group: ({ theme, isPromotion }) => css`
		${CurrentPrice} {
			font-size: 0.7em;
		}
		${Price} {
			font-size: 1.3em;
			font-weight: ${theme.font.normal};
		}
		${PricePeriod} {
			font-size: 0.86em;
			margin-bottom: 0;
			color: ${isPromotion ? theme.configurator.color.contrast : rgba(theme.configurator.color.primary, 0.9)};
			font-weight: ${theme.font.light};
		}
		${PriceDisclaimer} {
			font-size: 0.7em;
		}
	`,
	table: () => css`
		width: auto;
	`
};

export const PriceContainer = styled.div<ContainerProps>`
	${flex({ fd: 'column', ai: 'flex-start' })};
	width: ${({ width }) => width}%;
	font-size: 1.6rem;

	.skeleton-wrapper {
		width: 100%;
	}

	${customMinResponsive(
		size.xsmall,
		css`
			font-size: 1.7rem;
		`
	)}

	${customMinResponsive(
		size.semi,
		css`
			font-size: 1.85rem;
		`
	)}

	${customMinResponsive(
		1650,
		css`
			font-size: 2rem;
		`
	)}

	${applyStyleModifiers(PRICE_INFO_MODIFIERS as ModifiersConfig)};
`;

export const Price = styled.p<{ isPromotion?: boolean }>`
	${flex()}
	color: ${({ theme, isPromotion = false }) => (isPromotion ? theme.configurator.color.contrast : theme.configurator.color.primary)};
	line-height: normal;
	font-weight: ${({ theme }) => theme.font.light};
`;

export const PricePeriod = styled.span`
	color: ${({ theme }) => rgba(theme.configurator.color.primary, 0.9)};
	align-self: flex-end;
	line-height: normal;
	margin-bottom: 0.15em;
	margin-left: 0.3em;
	display: inline-block;
`;

export const CurrentPrice = styled.p`
	color: ${({ theme }) => rgba(theme.configurator.color.primary, 0.5)};
	text-decoration: line-through;
	font-weight: ${({ theme }) => theme.font.light};
`;

export const PriceDisclaimer = styled.p`
	color: ${({ theme }) => theme.configurator.color.secondary};
	line-height: 1.3em;
	margin-top: 0.8em;
	font-weight: ${({ theme }) => theme.font.light};
`;
